import { Field, Label } from '@headlessui/react';
import { FC } from 'react';

import { Valuation } from '../../../types/events.types';
import { ApprovalVestingTask } from '../../../types/vesting-tasks.types';
import { getHurdleExercisableShares } from '../../../utils/hurdle-exercisable-shares.util';
import { toNumber } from '../../../utils/to-number.util';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import { Checkbox } from '../../checkbox/index';
import { DatePicker } from '../../date-picker/index';
import { Input } from '../../input/index';
import { TableRow } from '../../table/index';
import { ApproveHurdleColumn, approveHurdleColumnMap } from './types';

export type HurdleTableRowProps = {
  approvalDate: Date;
  isSelected: boolean;
  issuedShares: string;
  onApprovalDateChange: (date: Date) => void;
  onIssuedSharesChange: (value: string) => void;
  onRemoveSelect: (stakeholderId: string) => void;
  onSelect: (vestingTask: ApprovalVestingTask) => void;
  valuation: Valuation;
  vestingTask: ApprovalVestingTask;
};

export const HurdleTableRow: FC<HurdleTableRowProps> = ({
  approvalDate,
  isSelected,
  issuedShares,
  onApprovalDateChange,
  onIssuedSharesChange,
  onRemoveSelect,
  onSelect,
  vestingTask,
  valuation,
}) => {
  const {
    id,
    grantItem: {
      id: grantId,
      stakeholder: { fullName },
      plan: {
        hardleValue: hurdleValue,
        pool: { shareClass },
      },
    },
  } = vestingTask;

  // Approving a hurdle exercises vested-but-unexercised shares; the input defaults to the amount
  // the stakeholder requested (modal-owned) and anything typed above the exercisable balance is
  // clamped on submit — the backend rejects more with "Insufficient exercisable shares".
  const exercisableShares = getHurdleExercisableShares(vestingTask);
  const availableBalance = Math.max(exercisableShares - toNumber(issuedShares), 0);

  return (
    <TableRow
      columnOrder={approveHurdleColumnMap}
      contentClassName="pl-4"
      fields={{
        id,
        [ApproveHurdleColumn.SELECT]: (
          <Field className="flex h-full w-full items-center">
            <Checkbox
              checked={isSelected}
              id={id}
              onChange={(checked) => {
                checked ? onSelect(vestingTask) : onRemoveSelect(id);
              }}
            />
            <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
          </Field>
        ),
        [ApproveHurdleColumn.STAKEHOLDER]: fullName,
        [ApproveHurdleColumn.GRANT]: grantId.split('-')[0].toUpperCase(),
        [ApproveHurdleColumn.APPROVAL_DATE]: (
          <DatePicker
            className="border-b border-gray-700 bg-gray-50 px-3 py-2"
            onSelect={onApprovalDateChange}
            value={approvalDate}
          />
        ),
        [ApproveHurdleColumn.SHARE_CLASS]: shareClass.name,
        [ApproveHurdleColumn.HURDLE]: `${hurdleValue?.toLocaleString('en-US')} $`,
        [ApproveHurdleColumn.CURRENT_VALUATION]: `${(
          toNumber(valuation.sharePrice) *
          toNumber(valuation.issuedShares || valuation.issuedSharesOnStart)
        ).toLocaleString('en-US')} $`,
        [ApproveHurdleColumn.ISSUED_SHARES]: (
          <div className="flex flex-col gap-2">
            <Input
              inputClassName="h-10"
              numberOnly
              onChange={onIssuedSharesChange}
              shouldFormatNumber
              value={issuedShares}
              wrapperClassName="w-full"
            />
            <span className="text-xs font-[450] text-gray-600">
              <AppFormattedMessage id="AVAILABLE" /> {availableBalance.toLocaleString('en-US')}
            </span>
          </div>
        ),
      }}
      trClassName={isSelected && 'bg-gray-50'}
    />
  );
};
